/*! JointJS+ v4.0.1 - HTML5 Diagramming Framework - TRIAL VERSION

Copyright (c) 2024 client IO

 2024-10-28


This Source Code Form is subject to the terms of the JointJS+ Trial License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/

import * as joint from '@joint/plus';
import {ProceduresService} from "../procedures/procedures.service";
import {firstValueFrom} from "rxjs";
import {AnnexesService} from "../annexes/annexes.service";
import {util} from "@joint/core";
import uuid = util.uuid;
import {ActivatedRoute} from "@angular/router";

export class InspectorService {
  inspector: joint.ui.Inspector;
  opts: any;
  procedures: any;
  annexes: any;
  currentLogigramme: any;
  elementDiagrammeNames: any;

  constructor(public procedureService: ProceduresService, public annexeService: AnnexesService, public logigramme: any) {
  }

  async create(cell: joint.dia.Cell, allCells: any): Promise<joint.ui.Inspector> {
    const currentLogigramme = allCells;
    
    this.procedures = await firstValueFrom(this.procedureService.getProcedures());

    this.procedures = this.procedures?.data?.map((procedure: any) => {
      return {value: procedure.id, content: procedure.titre, id: uuid()};
    });

    const currentProcedure = this.procedures.find((procedure: any) => procedure.value === cell.prop('attrs/label/procedureSearch'));

    this.annexes = await firstValueFrom(this.annexeService.getAnnexes());
    this.annexes = this.annexes?.data?.map((annexe: any) => {
      return {value: annexe.docLink, content: annexe.titre};
    });

    const currentAnnexe = this.annexes.find((annexe: any) => {
      const annexeSearch = cell.prop('attrs/label/annexeSearch');

      if (!annexeSearch) {
        return false;
      }
      const regex = /\/([^\/?]+\.pdf)/;
      const match = annexeSearch.match(regex);

      if (match) {
        let fileName = match[1];
        fileName = fileName.replace(/_/g, ' ');

        return fileName === annexe.content;

      } else {
        console.log("No match found");
      }

      return annexeSearch?.includes(annexe.content);

    });
    // On récupère l'ensemble des élèments
    // On les trie du plus haut au plus bas
    if(currentLogigramme && currentLogigramme.length > 0) {
      this.elementDiagrammeNames = currentLogigramme
        .filter((cell: any) => cell.attributes?.type !== "app.Link")
        .map((cell: any) => {
          let currentLabel = cell.attributes?.attrs?.label?.text;
          if(!currentLabel){
            currentLabel = cell.attributes?.defaultText;
          }
          return { value: cell.id, content:currentLabel, y: cell.position?.y}
        })
        .sort((cell_1: { y: number; }, cell_2: { y: number; }) => cell_1.y - cell_2.y);
    } else {
      this.elementDiagrammeNames = [];
    }

    const currentDiagrammeElementName = this.elementDiagrammeNames.find((element: any) => element.value === cell.prop('attrs/label/elementDiagrammeSearch'));

    const {groups, inputs} = this.getInspectorConfig()[cell.get('type')];

    this.inspector = joint.ui.Inspector.create('.inspector-container', {
        cell, groups, inputs,
        renderFieldContent: function (options: any, path: any, value: any, inspector: any): any {
          switch (options.type) {
            case 'procedure-select': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const input = document.createElement('input');
              const select = document.createElement('select');
              const defaultOption = document.createElement('option');

              label.textContent = options.label || 'Select';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              input.type = 'text';
              input.placeholder = 'Saisir un titre...';
              input.style.color = 'black';
              input.style.marginBottom = '5px';


              if (currentProcedure) {
                defaultOption.value = currentProcedure.value;
                defaultOption.text = currentProcedure.content;
              } else {
                defaultOption.value = '';
                defaultOption.text = 'Pas de resultats...';
              }
              defaultOption.style.display = 'none';
              select.appendChild(defaultOption);

              const updateSelectOptions = (filter: string) => {
                select.innerHTML = '';
                let hasResults = false;
                options.options.forEach((opt: any) => {
                  const option = document.createElement('option');
                  option.value = opt.value;
                  option.text = opt.content;
                  if (opt.content.toLowerCase().includes(filter)) {
                    select.appendChild(option);
                    hasResults = true;
                  }
                });
                if (!hasResults) {
                  select.appendChild(defaultOption);
                }

                if (select.options.length === 1) {
                  const event = new Event('change');
                  select.dispatchEvent(event);
                }
              };

              input.addEventListener('input', function () {
                const filter = input.value.toLowerCase();
                updateSelectOptions(filter);
              });

              select.addEventListener('change', function () {
                if(select.options?.length > 1)  {
                  inspector.updateCell(select, path, options);
                }
              });

              if (select.options.length === 1) {
                const event = new Event('change');
                select.dispatchEvent(event);
              }

              container.appendChild(label);
              container.appendChild(input);
              container.appendChild(select);

              return container;
            }
            case 'annexe-select': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const input = document.createElement('input');
              const select = document.createElement('select');
              const defaultOption = document.createElement('option');

              label.textContent = options.label || 'Select';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              input.type = 'text';
              input.placeholder = 'Saisir une annexes...';
              input.style.color = 'white';
              input.style.marginBottom = '5px';

              select.style.color = 'white';

              if (currentAnnexe) {
                defaultOption.value = currentAnnexe.value;
                defaultOption.text = currentAnnexe.content;
              } else {
                defaultOption.value = '';
                defaultOption.text = 'Pas de resultats...';
              }
              defaultOption.style.display = 'none';
              select.appendChild(defaultOption);

              const updateSelectOptions = (filter: string) => {
                select.innerHTML = '';
                let hasResults = false;
                options.options.forEach((opt: any) => {
                  const option = document.createElement('option');
                  option.value = opt.value;
                  option.text = opt.content;
                  if (opt.content.toLowerCase().includes(filter)) {
                    select.appendChild(option);
                    hasResults = true;
                  }
                });
                if (!hasResults) {
                  select.appendChild(defaultOption);
                }

                if (select.options.length === 1) {
                  const event = new Event('change');
                  select.dispatchEvent(event);
                }
              };

              input.addEventListener('input', function () {
                const filter = input.value.toLowerCase();
                updateSelectOptions(filter);
              });

              //select.value = value || 'none';
              select.addEventListener('change', function () {
                if (select.options.length > 1) {
                  inspector.updateCell(select, path, options);
                }
              });

              if (select.options.length === 1) {
                const event = new Event('change');
                select.dispatchEvent(event);
              }


              container.appendChild(label);
              container.appendChild(input);
              container.appendChild(select);

              return container;
            }
            case 'element-diagramme-select': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const input = document.createElement('input');
              const select = document.createElement('select');
              const defaultOption = document.createElement('option');

              label.textContent = options.label || 'Select';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              input.type = 'text';
              input.placeholder = "Saisir le label de l'élément";
              input.style.marginBottom = '5px';

              if (currentDiagrammeElementName) {
                defaultOption.value = currentDiagrammeElementName.value;
                defaultOption.text = currentDiagrammeElementName.content;
              } else {
                defaultOption.value = '';
                defaultOption.text = "Pas d'option choisies";
                // on met les options à jour avec les valeurs des elements du diagramme
                options.options.forEach((opt: any) => {
                  const option = document.createElement('option');
                  option.value = opt.value;
                  option.text = opt.content;
                  select.appendChild(option);
                })
              }
              defaultOption.style.display = 'none';
              select.appendChild(defaultOption);

              const updateSelectOptions = (filter: string) => {
                // Si aucun filtre, on met les options à jour avec les valeurs des elements du diagramme
                if(!filter) {
                  options.options.forEach((opt: any) => {
                    if(opt.content) {
                      const option = document.createElement('option');
                      option.value = opt.value;
                      option.text = opt.content;
                      select.appendChild(option);
                    }
                  })
                  // Sinon, on filtre normalement avec la valeur de l'input
                } else {
                  select.innerHTML = '';
                  let hasResults = false;
                  options.options.forEach((opt: any) => {
                    const option = document.createElement('option');
                    option.value = opt.value;
                    option.text = opt.content;
                    if (opt.content?.toLowerCase().includes(filter.toLowerCase())) {
                      select.appendChild(option);
                      hasResults = true;
                    }
                  });

                  if (!hasResults) {
                    select.appendChild(defaultOption);
                  }

                  if (select.options.length === 1) {
                    const event = new Event('change');
                    select.dispatchEvent(event);
                  }
                }
              };

              input.addEventListener('input', function () {
                const filter = input.value.toLowerCase();
                updateSelectOptions(filter);
              });

              //select.value = value || 'none';
              select.addEventListener('change', function () {
                if(select.options?.length > 1)  {
                  inspector.updateCell(select, path, options);
                }
              });

              if (select.options.length === 1) {
                const event = new Event('change');
                select.dispatchEvent(event);
              }

              container.appendChild(label);
              container.appendChild(input);
              container.appendChild(select);

              return container;
            }
            case 'procedure-value': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const valueText = document.createElement('span');

              label.textContent = options.label || 'Procedure Value';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              valueText.textContent = value || '';
              valueText.style.color = 'white';

              container.appendChild(label);
              container.appendChild(valueText);

              return container;
            }
            case 'annexe-value': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const valueText = document.createElement('span');

              label.textContent = options.label || 'Annexe Value';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              valueText.textContent = value || '';
              valueText.style.color = 'white';

              container.appendChild(label);
              container.appendChild(valueText);

              return container;
            }
            case 'element-diagramme-value': {
              const container = document.createElement('div');
              const label = document.createElement('label');
              const valueText = document.createElement('span');

              label.textContent = options.label || 'Élement diagramme Value';
              label.style.display = 'block';
              label.style.marginBottom = '5px';

              valueText.textContent = value || '';
              valueText.style.color = 'white';

              container.appendChild(label);
              container.appendChild(valueText);
              return container;
            }
          }
        },
        getFieldValue: function (attribute: any, type: any): any {
          switch (type) {
            case 'procedure-select': {
              return attribute.querySelector('select').value;
            }
            case 'annexe-select': {
              return attribute.querySelector('select').value;
            }
            case 'element-diagramme-select': {
              return attribute.querySelector('select').value;
            }
            case 'procedure-value': {
              return attribute.querySelector('span').textContent;
            }
            case 'annexe-value': {
              return attribute.querySelector('span').textContent;
            }
            case 'element-diagramme-value': {
              return attribute.querySelector('span').textContent;
            }
          }
        }
      }
    );
    //this.inspector.focusField('attrs/label/text');

    this.inspector.on('change:attrs/label/type', (cellView: any, value: string) => {
      if (cellView === 'information') {
        cell.prop('attrs/label/informationText', '');
        cell.removeProp('attrs/label/lienVers');
        cell.removeProp('attrs/label/procedureSearch');
        cell.removeProp('attrs/label/procedureValue');
        cell.removeProp('attrs/label/annexeSearch');
        cell.removeProp('attrs/label/annexeValue');
        cell.removeProp('attrs/label/elementDiagrammeSearch');
        cell.removeProp('attrs/label/elementDiagrammeValue');
      } else if (cellView === 'jonction') {
        cell.prop('attrs/label/lienVers', '');
        cell.removeProp('attrs/label/informationText');
      }
    });

    this.inspector.on('change:attrs/label/procedureSearch', (cellView: any, value: string) => {
      cell.prop('attrs/label/procedureSearch', cellView);
    })

    this.inspector.on('change:attrs/label/procedureValue', (cellView: any, value: string) => {
      cell.prop('attrs/label/procedureValue', cellView);
    })

    this.inspector.on('change:attrs/label/elementDiagrammeSearch', (cellView: any, value: string) => {
      cell.prop('attrs/label/elementDiagrammeSearch', cellView);
    })

    this.inspector.on('change:attrs/label/elementDiagrammeValue', (cellView: any, value: string) => {
      cell.prop('attrs/label/elementDiagrammeValue', cellView);
    })

    /*
    this.inspector.on(`change:attrs/label/textUnderline`, (valueToggle: boolean, inputEl) => {
      // si le toggle est coché.
      if(valueToggle) {
        cell.prop('attrs/label/textDecoration', 'underline');  // Underline the text
      } else {
        cell.prop('attrs/label/textDecoration', 'none');
      }
    })
     */

    return this.inspector;
  }


  getInspectorConfig() {
    const procedureSearchInit = [
      {value: 'procedure1', content: 'Procedure 1'},
      {value: 'procedure2', content: 'Procedure 2'},
      {value: 'procedure3', content: 'Procedure 3'},
    ]

    const options = {

      colorPalette: [
        {content: 'transparent', icon: '/assets/transparent-icon.svg'},
        {content: '#f6f6f6'},
        {content: '#dcd7d7'},
        {content: '#8f8f8f'},
        {content: '#c6c7e2'},
        {content: '#feb663'},
        {content: '#fe854f'},
        {content: '#b75d32'},
        {content: '#31d0c6'},
        {content: '#7c68fc'},
        {content: '#61549c'},
        {content: '#6a6c8a'},
        {content: '#4b4a67'},
        {content: '#3c4260'},
        {content: '#33334e'},
        {content: '#222138'}
      ],

      colorPaletteReset: [
        {content: <string>undefined!, icon: '/assets/no-color-icon.svg'},
        {content: '#f6f6f6'},
        {content: '#dcd7d7'},
        {content: '#8f8f8f'},
        {content: '#c6c7e2'},
        {content: '#feb663'},
        {content: '#fe854f'},
        {content: '#b75d32'},
        {content: '#31d0c6'},
        {content: '#7c68fc'},
        {content: '#61549c'},
        {content: '#6a6c8a'},
        {content: '#4b4a67'},
        {content: '#3c4260'},
        {content: '#33334e'},
        {content: '#222138'}
      ],

      fontWeight: [
        {value: '300', content: '<span style="font-weight: 300">Light</span>'},
        {value: 'Normal', content: '<span style="font-weight: Normal">Normale</span>'},
        {value: 'Bold', content: '<span style="font-weight: Bolder">Gras</span>'}
      ],

      fontFamily: [
        {value: 'Alegreya Sans', content: '<span style="font-family: Alegreya Sans">Alegreya Sans</span>'},
        {value: 'Averia Libre', content: '<span style="font-family: Averia Libre">Averia Libre</span>'},
        {value: 'Roboto Condensed', content: '<span style="font-family: Roboto Condensed">Roboto Condensed</span>'}
      ],

      strokeStyle: [
        {value: '0', content: 'Solid'},
        {value: '2,5', content: 'Dotted'},
        {value: '10,5', content: 'Dashed'}
      ],

      side: [
        {value: 'top', content: 'Top Side'},
        {value: 'right', content: 'Right Side'},
        {value: 'bottom', content: 'Bottom Side'},
        {value: 'left', content: 'Left Side'}
      ],

      portLabelPositionRectangle: [
        {value: {name: 'top', args: {y: -12}}, content: 'Above'},
        {value: {name: 'right', args: {y: 0}}, content: 'On Right'},
        {value: {name: 'bottom', args: {y: 12}}, content: 'Below'},
        {value: {name: 'left', args: {y: 0}}, content: 'On Left'}
      ],

      portLabelPositionEllipse: [
        {value: 'radial', content: 'Horizontal'},
        {value: 'radialOriented', content: 'Angled'}
      ],

      imageIcons: [
        {value: '/assets/image-icon1.svg', content: '<img height="42px" src="assets/image-icon1.svg"/>'},
        {value: '/assets/image-icon2.svg', content: '<img height="80px" src="assets/image-icon2.svg"/>'},
        {value: '/assets/image-icon3.svg', content: '<img height="80px" src="assets/image-icon3.svg"/>'},
        {value: '/assets/image-icon4.svg', content: '<img height="80px" src="assets/image-icon4.svg"/>'}
      ],

      imageGender: [
        {
          value: '/assets/member-male.png',
          content: '<img height="50px" src="assets/member-male.png" style="margin: 5px 0 0 2px;"/>'
        },
        {
          value: '/assets/member-female.png',
          content: '<img height="50px" src="assets/member-female.png" style="margin: 5px 0 0 2px;"/>'
        }
      ],

      arrowheadSize: [
        {value: 'M 0 0 0 0', content: 'None'},
        {value: 'M 0 -3 -6 0 0 3 z', content: 'Small'},
        {value: 'M 0 -5 -10 0 0 5 z', content: 'Medium'},
        {value: 'M 0 -10 -15 0 0 10 z', content: 'Large'},
      ],

      strokeWidth: [
        {value: 1, content: '<div style="background:#fff;width:2px;height:30px;margin:0 14px;border-radius: 2px;"/>'},
        {value: 2, content: '<div style="background:#fff;width:4px;height:30px;margin:0 13px;border-radius: 2px;"/>'},
        {value: 4, content: '<div style="background:#fff;width:8px;height:30px;margin:0 11px;border-radius: 2px;"/>'},
        {value: 8, content: '<div style="background:#fff;width:16px;height:30px;margin:0 8px;border-radius: 2px;"/>'}
      ],

      router: [
        {
          value: 'normal',
          content: '<p style="background:#fff;width:2px;height:30px;margin:0 14px;border-radius: 2px;"/>'
        },
        {
          value: 'orthogonal',
          content: '<p style="width:20px;height:30px;margin:0 5px;border-bottom: 2px solid #fff;border-left: 2px solid #fff;"/>'
        },
        {
          value: 'oneSide',
          content: '<p style="width:20px;height:30px;margin:0 5px;border: 2px solid #fff;border-top: none;"/>'
        }
      ],

      connector: [
        {
          value: 'normal',
          content: '<p style="width:20px;height:20px;margin:5px;border-top:2px solid #fff;border-left:2px solid #fff;"/>'
        },
        {
          value: 'rounded',
          content: '<p style="width:20px;height:20px;margin:5px;border-top-left-radius:30%;border-top:2px solid #fff;border-left:2px solid #fff;"/>'
        },
        {
          value: 'smooth',
          content: '<p style="width:20px;height:20px;margin:5px;border-top-left-radius:100%;border-top:2px solid #fff;border-left:2px solid #fff;"/>'
        }
      ],

      labelPosition: [
        {value: 30, content: 'Close to source'},
        {value: 0.5, content: 'In the middle'},
        {value: -30, content: 'Close to target'},
      ],

      typeAction: [
        {value: 'information', content: 'Information'},
      ],

      typeActionJonction: [
        {value: 'jonction', content: 'Jonction'}
      ],

      LienVers: [
        {value: 'procedure', content: 'Procédure'},
        {value: 'annexe', content: 'Annexe'},
        {value: 'element', content: 'Element du logigramme'},
      ],

      procedureSearch: procedureSearchInit,

      searchProcedureOptions(searchTerm: any) {
        return procedureSearchInit.filter(option =>
          option.content.toLowerCase().includes(searchTerm?.value.toLowerCase())
        );
      },

      updateOptions(newOptions: any[]) {
        this.procedureSearch = newOptions;
      },

      portMarkup: [{
        value: [{
          tagName: 'rect',
          selector: 'portBody',
          attributes: {
            'width': 20,
            'height': 20,
            'x': -10,
            'y': -10
          }
        }],
        content: 'Rectangle'
      }, {
        value: [{
          tagName: 'circle',
          selector: 'portBody',
          attributes: {
            'r': 10
          }
        }],
        content: 'Circle'
      }, {
        value: [{
          tagName: 'path',
          selector: 'portBody',
          attributes: {
            'd': 'M -10 -10 10 -10 0 10 z'
          }
        }],
        content: 'Triangle'
      }]
    };
    this.opts = options;

    return <{ [index: string]: any }>{

      'app.Link': {
        inputs: {
          attrs: {
            line: {
              strokeWidth: {
                type: 'select-button-group',
                options: options.strokeWidth,
                group: 'connection',
                label: 'Link thickness',
                when: {ne: {'attrs/line/stroke': 'transparent'}},
                index: 4
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                group: 'connection',
                label: 'Link style',
                when: {ne: {'attrs/line/stroke': 'transparent'}},
                index: 5
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                group: 'connection',
                label: 'Color',
                index: 6
              },
              sourceMarker: {
                d: {
                  type: 'select-box',
                  options: options.arrowheadSize,
                  group: 'marker-source',
                  label: 'Source arrowhead',
                  index: 1
                },
                fill: {
                  type: 'color-palette',
                  options: options.colorPaletteReset,
                  group: 'marker-source',
                  label: 'Color',
                  when: {ne: {'attrs/line/sourceMarker/d': 'M 0 0 0 0'}},
                  index: 2
                }
              },
              targetMarker: {
                d: {
                  type: 'select-box',
                  options: options.arrowheadSize,
                  group: 'marker-target',
                  label: 'Target arrowhead',
                  index: 1
                },
                fill: {
                  type: 'color-palette',
                  options: options.colorPaletteReset,
                  group: 'marker-target',
                  label: 'Color',
                  when: {ne: {'attrs/line/targetMarker/d': 'M 0 0 0 0'}},
                  index: 2
                }
              }
            }
          },
          router: {
            name: {
              type: 'select-button-group',
              options: options.router,
              group: 'connection',
              label: 'Connection type',
              index: 1
            },
            args: {
              side: {
                type: 'select-box',
                options: options.side,
                placeholder: 'Pick a side',
                group: 'connection',
                label: 'Anchors side',
                when: {eq: {'router/name': 'oneSide'}, otherwise: {unset: true}},
                index: 2
              }
            }
          },
          connector: {
            name: {
              type: 'select-button-group',
              options: options.connector,
              group: 'connection',
              label: 'Connection style',
              index: 3
            }
          },
          labels: {
            type: 'list',
            group: 'labels',
            label: 'Labels',
            attrs: {
              label: {
                'data-tooltip': 'Set (possibly multiple) labels for the link',
                'data-tooltip-position': 'right',
                'data-tooltip-position-selector': '.joint-inspector'
              }
            },
            item: {
              type: 'object',
              properties: {
                attrs: {
                  text: {
                    text: {
                      type: 'content-editable',
                      label: 'text',
                      defaultValue: 'label',
                      index: 1,
                      attrs: {
                        label: {
                          'data-tooltip': 'Set text of the label',
                          'data-tooltip-position': 'right',
                          'data-tooltip-position-selector': '.joint-inspector'
                        }
                      }
                    },
                    fill: {
                      type: 'color-palette',
                      options: options.colorPaletteReset,
                      label: 'Couleur du texte',
                      index: 5
                    }
                  },
                  rect: {
                    fill: {
                      type: 'color-palette',
                      options: options.colorPaletteReset,
                      label: 'Couleur',
                      index: 3
                    },
                    stroke: {
                      type: 'color-palette',
                      options: options.colorPaletteReset,
                      label: 'Bordure',
                      index: 4
                    }
                  }
                },
                position: {
                  type: 'select-box',
                  options: options.labelPosition || [],
                  defaultValue: 0.5,
                  label: 'Position',
                  placeholder: 'Custom',
                  index: 2,
                  attrs: {
                    label: {
                      'data-tooltip': 'Position the label relative to the source of the link',
                      'data-tooltip-position': 'right',
                      'data-tooltip-position-selector': '.joint-inspector'
                    }
                  }
                }
              }
            }
          }
        },
        groups: {
          connection: {
            label: 'Connection',
            index: 1
          },
          'marker-source': {
            label: 'Source marker',
            index: 2
          },
          'marker-target': {
            label: 'Target marker',
            index: 3
          },
          labels: {
            label: 'Labels',
            index: 4
          }
        }
      },
      'standard.Rectangle': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },

              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          },
        }
      },
      'standard.Ellipse': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4,
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/stroke-width': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false,
          }
        }
      },
      'standard.Polygon': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false,
          }
        }
      },
      'standard.Cylinder': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            },
            top: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'top',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'top',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'top',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'top',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          top: {
            label: 'Top',
            index: 2
          },
          text: {
            label: 'Texte',
            index: 3,
            closed: false,
          }
        }
      },
      'standard.Image': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            image: {
              xlinkHref: {
                type: 'content-editable',
                options: options.imageIcons,
                label: "Url de l'image",
                group: 'presentation',
                index: 1
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false,
          }
        }
      },
      'standard.InscribedImage': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            image: {
              xlinkHref: {
                type: 'select-box',
                options: options.imageIcons,
                label: 'Image',
                group: 'presentation',
                index: 1
              }
            },
            background: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 2
              }
            },
            border: {
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 3
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 10,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/border/stroke': 'transparent'}},
                index: 4
              }
            }
          },
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false,
          }
        }
      },
      'standard.EmbeddedImage': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            },
            image: {
              xlinkHref: {
                type: 'select-box',
                options: options.imageIcons,
                label: 'Image',
                group: 'image',
                index: 1
              }
            }
          }
        },
        groups: {
          image: {
            label: 'Image',
            index: 1,
            closed: true,
          },
          presentation: {
            label: 'Présentation',
            index: 2,
            closed: true,
          },
          text: {
            label: 'Texte',
            index: 3,
            closed: false,
          }
        }
      },
      'standard.HeaderedRectangle': {
        inputs: {
          attrs: {
            bodyText: {
              textWrap: {
                text: {
                  type: 'content-editable',
                  label: 'Wrapped text',
                  group: 'text',
                  index: 1
                }
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/bodyText/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/bodyText/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/bodyText/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/boduText/text': ''}},
                index: 5
              }
            },
            headerText: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'headerText',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'headerText',
                when: {ne: {'attrs/headerText/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'headerText',
                when: {ne: {'attrs/headerText/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'headerText',
                when: {ne: {'attrs/headerText/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'headerText',
                when: {ne: {'attrs/headerText/text': ''}},
                index: 5
              }
            },
            body: {
              fill: [{
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              }],
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            },
            header: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'header',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'header',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'header',
                when: {ne: {'attrs/header/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'header',
                when: {
                  and: [
                    {ne: {'attrs/header/stroke': 'transparent'}},
                    {ne: {'attrs/header/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false,
          },
          header: {
            label: 'Header',
            index: 3
          },
          headerText: {
            label: "Texte de l'entête",
            index: 4
          }
        }
      },
      'app.RectangularModel': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1,
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          },
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          }
        },
      },
      'app.RectangularRenvoiModel': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
              type: {
                type: 'select-box',
                options: options.typeActionJonction,
                label: 'Type',
                group: 'actions',
                index: 3,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                    cell.removeProp('attrs/label/lienVers');
                    cell.removeProp('attrs/label/procedureSearch');
                    cell.removeProp('attrs/label/procedureValue');
                    cell.removeProp('attrs/label/annexeSearch');
                    cell.removeProp('attrs/label/annexeValue');
                    cell.removeProp('attrs/label/elementDiagrammeSearch');
                    cell.removeProp('attrs/label/elementDiagrammeValue');
                    cell.removeProp('attrs/label/elementDiagramPosition');
                  } else if (value === 'jonction') {
                    cell.prop('attrs/label/lienVers', '');
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              informationText: {
                type: 'content-editable',
                label: "Texte d'information",
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'information'}
                },
                index: 4,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                  } else {
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              lienVers: {
                type: 'select-box',
                options: options.LienVers,
                label: 'Lien Vers',
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'jonction'}
                },
                index: 5,
                change: function (cell: any, value: any) {
                  cell.prop('attrs/label/LienVers', value);
                }
              },
              procedureValue: {
                type: 'procedure-value',
                label: 'Valeur de la procédure',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}},
                  ]
                },
                index: 6
              },
              procedureSearch: {
                type: 'procedure-select',
                options: this.procedures,
                label: 'Rechercher une procédure ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}}
                  ]
                },
                index: 7,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/procedureSearch', value);
                  if (value) {
                    cell.prop('attrs/label/procedureValue', value);
                  }
                }
              },
              annexeValue: {
                type: 'annexe-value',
                label: 'Valeur de l\'annexe',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}},
                  ]
                },
                index: 8
              },
              annexeSearch: {
                type: 'annexe-select',
                options: this.annexes,
                label: 'Rechercher une annexe ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}}
                  ]
                },
                index: 9,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/annexeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/annexeValue', value);
                  }
                }
              },
              elementDiagrammeValue: {
                type: 'element-diagramme-value',
                label: "Label de l'élément du diagramme",
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}},
                  ]
                },
                index: 10
              },
              elementDiagrammeSearch: {
                type: 'element-diagramme-select',
                options: this.elementDiagrammeNames,
                label: 'Rechercher un element du diagramme ( le tri est fait du plus haut au plus bas ) ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}}
                  ]
                },
                index: 11,
                change: (cell: joint.dia.Cell, value: any) => {
                  cell.prop('attrs/label/elementDiagrammeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/elementDiagrammeValue', value);
                  }
                }
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          },
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          },
          actions: {
            label: 'Actions',
            index: 3,
            closed: true
          }
        },
      },
      'app.CircularModel': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
              type: {
                type: 'select-box',
                options: options.typeAction,
                label: 'Type',
                group: 'actions',
                index: 3,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                  } else {
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              informationText: {
                type: 'content-editable',
                label: "Texte d'information",
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'information'}
                },
                index: 4,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                  } else {
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              lienVers: {
                type: 'select-box',
                options: options.LienVers,
                label: 'Lien Vers',
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'jonction'}
                },
                index: 5,
                change: function (cell: any, value: any) {
                  cell.prop('attrs/label/LienVers', value);
                }
              },
              procedureValue: {
                type: 'procedure-value',
                label: 'Valeur de la procédure',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}},
                  ]
                },
                index: 6
              },
              procedureSearch: {
                type: 'procedure-select',
                options: this.procedures,
                label: 'Rechercher une procédure ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}}
                  ]
                },
                index: 7,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/procedureSearch', value);
                  if (value) {
                    cell.prop('attrs/label/procedureValue', value);
                  }
                }
              },
              annexeValue: {
                type: 'annexe-value',
                label: 'Valeur de l\'annexe',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}},
                  ]
                },
                index: 8
              },
              annexeSearch: {
                type: 'annexe-select',
                options: this.annexes,
                label: 'Rechercher une annexe ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}}
                  ]
                },
                index: 9,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/annexeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/annexeValue', value);
                  }
                }
              },
              elementDiagrammeValue: {
                type: 'element-diagramme-value',
                label: "Label de l'élément du diagramme",
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}},
                  ]
                },
                index: 10
              },
              elementDiagrammeSearch: {
                type: 'element-diagramme-select',
                options: this.elementDiagrammeNames,
                label: 'Rechercher un element du diagramme ( le tri est fait du plus haut au plus bas ) ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}}
                  ]
                },
                index: 11,
                change: (cell: joint.dia.Cell, value: any) => {
                  cell.prop('attrs/label/elementDiagrammeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/elementDiagrammeValue', value);
                  }
                }
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          },
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          },
          actions: {
            label: 'Actions',
            index: 3,
            closed: true
          }
        },
      },
      'app.CircularModelNoAction': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          },
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          },
          actions: {
            label: 'Actions',
            index: 3,
            closed: true
          }
        },
      },
      'standard.Path': {
        inputs: {
          attrs: {
            label: {
              text: {
                type: 'content-editable',
                label: 'Texte',
                group: 'text',
                index: 1
              },
              fontSize: {
                type: 'range',
                min: 5,
                max: 80,
                unit: 'px',
                label: 'Taille de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 2
              },
              fontFamily: {
                type: 'select-box',
                options: options.fontFamily,
                label: 'Police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 3
              },
              /*
              fontWeight: {
                type: 'select-box',
                options: options.fontWeight,
                label: 'Épaisseur de la police',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
              textUnderline: {
                type: 'toggle',
                label: 'Surlignage',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 4
              },
               */
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'text',
                when: {ne: {'attrs/label/text': ''}},
                index: 5
              },
              type: {
                type: 'select-box',
                options: options.typeActionJonction,
                label: 'Type',
                group: 'actions',
                index: 3,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                    cell.removeProp('attrs/label/lienVers');
                    cell.removeProp('attrs/label/procedureSearch');
                    cell.removeProp('attrs/label/procedureValue');
                    cell.removeProp('attrs/label/annexeSearch');
                    cell.removeProp('attrs/label/annexeValue');
                    cell.removeProp('attrs/label/elementDiagrammeSearch');
                    cell.removeProp('attrs/label/elementDiagrammeValue');
                    cell.removeProp('attrs/label/elementDiagramPosition');
                  } else if (value === 'jonction') {
                    cell.prop('attrs/label/lienVers', '');
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              informationText: {
                type: 'content-editable',
                label: "Texte d'information",
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'information'}
                },
                index: 4,
                change: function (cell: any, value: any) {
                  if (value === 'information') {
                    cell.prop('attrs/label/informationText', '');
                  } else {
                    cell.removeProp('attrs/label/informationText');
                  }
                }
              },
              lienVers: {
                type: 'select-box',
                options: options.LienVers,
                label: 'Lien Vers',
                group: 'actions',
                when: {
                  eq: {'attrs/label/type': 'jonction'}
                },
                index: 5,
                change: function (cell: any, value: any) {
                  cell.prop('attrs/label/LienVers', value);
                }
              },
              procedureValue: {
                type: 'procedure-value',
                label: 'Valeur de la procédure',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}},
                  ]
                },
                index: 6
              },
              procedureSearch: {
                type: 'procedure-select',
                options: this.procedures,
                label: 'Rechercher une procédure ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'procedure'}}
                  ]
                },
                index: 7,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/procedureSearch', value);
                  if (value) {
                    cell.prop('attrs/label/procedureValue', value);
                  }
                }
              },
              annexeValue: {
                type: 'annexe-value',
                label: 'Valeur de l\'annexe',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}},
                  ]
                },
                index: 8
              },
              annexeSearch: {
                type: 'annexe-select',
                options: this.annexes,
                label: 'Rechercher une annexe ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'annexe'}}
                  ]
                },
                index: 9,
                change: (cell: joint.dia.Cell, value: string) => {
                  cell.prop('attrs/label/annexeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/annexeValue', value);
                  }
                }
              },
              elementDiagrammeValue: {
                type: 'element-diagramme-value',
                label: "Label de l'élément du diagramme",
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}},
                  ]
                },
                index: 10
              },
              elementDiagrammeSearch: {
                type: 'element-diagramme-select',
                options: this.elementDiagrammeNames,
                label: 'Rechercher un element du diagramme ( le tri est fait du plus haut au plus bas ) ',
                group: 'actions',
                when: {
                  and: [
                    {eq: {'attrs/label/lienVers': 'element'}}
                  ]
                },
                index: 11,
                change: (cell: joint.dia.Cell, value: any) => {
                  cell.prop('attrs/label/elementDiagrammeSearch', value);
                  if (value) {
                    cell.prop('attrs/label/elementDiagrammeValue', value);
                  }
                }
              }
            },
            body: {
              fill: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Couleur',
                group: 'presentation',
                index: 1
              },
              stroke: {
                type: 'color-palette',
                options: options.colorPalette,
                label: 'Bordure',
                group: 'presentation',
                index: 2
              },
              strokeWidth: {
                type: 'range',
                min: 0,
                max: 30,
                step: 1,
                defaultValue: 1,
                unit: 'px',
                label: 'Épaisseur de la bordure',
                group: 'presentation',
                when: {ne: {'attrs/body/stroke': 'transparent'}},
                index: 3
              },
              strokeDasharray: {
                type: 'select-box',
                options: options.strokeStyle,
                label: 'Style de la bordure',
                group: 'presentation',
                when: {
                  and: [
                    {ne: {'attrs/body/stroke': 'transparent'}},
                    {ne: {'attrs/body/strokeWidth': 0}}
                  ]
                },
                index: 4
              }
            }
          }
        },
        groups: {
          presentation: {
            label: 'Présentation',
            index: 1,
            closed: true
          },
          text: {
            label: 'Texte',
            index: 2,
            closed: false
          },
          actions: {
            label: 'Actions',
            index: 3,
            closed: true,
            when: {
              eq: { 'condition': 'renvoi' }
            }
          }
        },
      },

    };
  }

}


